import type { ProfileData } from "@recursive-robot/react-jsx-parser";
import type { CycleEntry } from "../../../store/profiling/types";

// Sequential blue heat ramp (light -> dark), from the data-viz reference palette.  Darker == hotter
// (more self-time).  Interpolated in JS so it renders identically in the pop-out window regardless of
// which stylesheet copy loaded; text on top is chosen by luminance so it always clears contrast.
const HEAT_RAMP: [number, number, number][] = [
    [0xcd, 0xe2, 0xfb], // 100
    [0x86, 0xb6, 0xef], // 250
    [0x39, 0x87, 0xe5], // 400
    [0x1c, 0x5c, 0xab], // 550
    [0x0d, 0x36, 0x6b], // 700
];

function lerp(a: number, b: number, t: number): number {
    return Math.round(a + (b - a) * t);
}

/** Map a normalized heat value t ∈ [0,1] to an `rgb(...)` string on the blue ramp. */
export function heatColor(t: number): string {
    const clamped = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
    const span = HEAT_RAMP.length - 1;
    const scaled = clamped * span;
    const i = Math.min(span - 1, Math.floor(scaled));
    const local = scaled - i;
    const [r1, g1, b1] = HEAT_RAMP[i];
    const [r2, g2, b2] = HEAT_RAMP[i + 1];
    return `rgb(${lerp(r1, r2, local)}, ${lerp(g1, g2, local)}, ${lerp(b1, b2, local)})`;
}

/** Ink or white for text drawn on top of a heat fill, chosen for contrast. */
export function textOnHeat(t: number): string {
    // Perceived luminance falls as t grows (ramp darkens); switch to white past the midpoint.
    return t > 0.45 ? "#ffffff" : "#0b0b0b";
}

/** Categorical lane colors for the timeline (blue / aqua / violet from the reference palette). */
export const TRIGGER_COLORS: Record<ProfileData["trigger"], string> = {
    render: "#2a78d6",
    callback: "#1baf7a",
    react: "#4a3aa7",
};

export const TRIGGER_LABELS: Record<ProfileData["trigger"], string> = {
    render: "Render (parser walk)",
    callback: "Callback (render-prop)",
    react: "React commit",
};

/** Format a millisecond duration with adaptive precision. */
export function formatMs(ms: number): string {
    if (ms == null || !Number.isFinite(ms)) return "—";
    if (ms >= 100) return `${ms.toFixed(0)} ms`;
    if (ms >= 1) return `${ms.toFixed(2)} ms`;
    return `${ms.toFixed(3)} ms`;
}

export function shortCycleId(cycleId: string): string {
    // The cycleId is `<ISO UTC timestamp>-<hash>-<counter>`; the timestamp prefix is the meaningful,
    // pass-distinguishing part. Parse it and show the time-of-day in the viewer's LOCAL timezone
    // (e.g. "14:24:05.887") rather than the raw UTC substring.
    const iso = cycleId.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)/);
    if (iso) {
        const d = new Date(iso[1]);
        if (!Number.isNaN(d.getTime())) {
            const pad = (n: number, len = 2) => String(n).padStart(len, "0");
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
        }
    }
    // Fall back to the leading portion (identity lives in the prefix, not the tail).
    return cycleId.length > 14 ? `${cycleId.slice(0, 14)}…` : cycleId;
}

/** All batches of a cycle in a stable order: render, then callbacks (by renderId), then react. */
export function cycleBatches(entry: CycleEntry): ProfileData[] {
    const batches: ProfileData[] = [];
    if (entry.render) batches.push(entry.render);
    batches.push(...[...entry.callbacks].sort((a, b) => a.renderId - b.renderId));
    if (entry.react) batches.push(entry.react);
    return batches;
}

/** A globally unique batch key — the timeline's Template scope mixes batches of several cycles, so
 * the cycleId qualifier is load-bearing (trigger+renderId alone only distinguish within a cycle). */
export function batchKey(batch: ProfileData): string {
    return `${batch.cycleId}:${batch.trigger}:${batch.renderId}`;
}

export type CycleSummary = {
    renderTotal: number;
    renderNodes: number;
    callbackCount: number;
    callbackTotal: number;
    callbackNodes: number;
    reactTotal: number;
    reactNodes: number;
    totalNodes: number;
    wallTime: number;
};

/** Roll up per-trigger timing / node counts for a captured render pass (used by tooltips). */
export function summarizeCycle(entry: CycleEntry): CycleSummary {
    const renderTotal = entry.render?.totalTime ?? 0;
    const renderNodes = entry.render?.nodes.length ?? 0;
    const callbackCount = entry.callbacks.length;
    const callbackTotal = entry.callbacks.reduce((s, b) => s + b.totalTime, 0);
    const callbackNodes = entry.callbacks.reduce((s, b) => s + b.nodes.length, 0);
    const reactTotal = entry.react?.totalTime ?? 0;
    const reactNodes = entry.react?.nodes.length ?? 0;

    // Wall-clock span of the pass: from the earliest batch start to the latest batch end.
    const batches = cycleBatches(entry);
    let min = Infinity, max = -Infinity;
    for (const b of batches) {
        min = Math.min(min, b.startTime);
        max = Math.max(max, b.startTime + b.totalTime);
    }
    const wallTime = Number.isFinite(min) ? max - min : 0;

    return {
        renderTotal, renderNodes, callbackCount, callbackTotal, callbackNodes,
        reactTotal, reactNodes, totalNodes: renderNodes + callbackNodes + reactNodes, wallTime,
    };
}
